/**
 * Certificates — every certificate this academy has issued, and the levers over them.
 *
 * NOT one course at a time, which is where this page started and why it was wrong. "Has she
 * got her certificate?", "who is waiting on one?", "did that re-run work?" — none of those
 * questions begin with picking a course out of a hundred, and until one was picked this page
 * showed nothing at all. So it opens on the academy and the course becomes a filter.
 *
 * THE LIST IS ONE REQUEST TO `academy/certificates`, where it used to be two endpoints joined
 * across up to 34 requests: ~17 to `/api/instructor/v2/courses/{id}/certificates/issued` (one
 * per course) for the complete record per course, then ~12 to
 * `/api/certificates/v0/certificates/{username}/` (one per holder) for the grade and issue date
 * the first call did not carry. `GeneratedCertificate` holds every field both endpoints returned
 * between them, so the server answers with one row per certificate rather than two halves the
 * browser had to stitch together.
 *
 * ONE REAL DIFFERENCE, AND IT IS AN IMPROVEMENT: the per-holder endpoint this replaced was
 * FILTERED BY THE PLATFORM to certificates a learner may currently see, so a certificate that
 * existed but was held back showed a blank grade and no issue date — "no grade shown" and "held
 * back" used to be the same row for that reason. Reading the table directly returns every
 * certificate's real status, grade and issue date regardless, so there is no more gap to explain
 * and the "Issued" column below no longer has a "not readable yet" state.
 *
 * Regeneration and the generation history stay per course, because that is what they act on,
 * and they appear once a course is chosen. Regeneration is destructive-adjacent — it reissues
 * certificates people may already have downloaded and linked to — so the statuses are an
 * explicit choice and nothing happens on a single click.
 *
 * WHAT THIS PAGE IS NOT. Configuring what a certificate SAYS — its title, description and
 * signatories — belongs to the course and lives on the course's own Certificates tab.
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Banner,
  Button,
  ConfirmDialog,
  DataTable,
  Kpi,
  KpiRow,
  Pill,
  SearchField,
  Spinner,
  Tabs,
  Toolbar,
  type Column,
} from '../../shared/ui';
import { listAcademyCertificates, type AcademyCertificate, type AcademyCourse } from '../../shared/lib/academy';
import {
  getCertificateConfig,
  listGenerationHistory,
  regenerateCertificates,
  type GenerationHistoryEntry,
} from '../../shared/lib/instructor';
import { certificateViewUrl, formatIssued, gradePercent } from '../../shared/lib/certificates';
import { PageHeader } from '../AdminShell';
import { CourseCell, CourseSwitcher, courseChoices, useCourseParam } from '../CourseFilter';
import { useAcademyCourses } from '../useAcademyCourses';

/** The platform's own statuses, and what each means for a re-run. */
const REGENERATE_STATUSES: { value: string; label: string; hint: string }[] = [
  { value: 'downloadable', label: 'Already issued', hint: 'Replaces certificates people already hold.' },
  { value: 'notpassing', label: 'Not passing', hint: 'For learners whose grade has since changed.' },
  { value: 'error', label: 'Errored', hint: 'Generation failed for these; safe to retry.' },
];

type Shelf = 'all' | 'available' | 'held' | 'invalidated';

/** A certificate somebody can open. Revoked ones do not count however good the status looks. */
const isAvailable = (row: AcademyCertificate) =>
  row.certificate_status === 'downloadable' && !row.invalidated_by;

export default function AdminCertificates() {
  const [course, setCourse] = useCourseParam();
  const [shelf, setShelf] = useState<Shelf>('all');
  const [search, setSearch] = useState('');

  const courses = useAcademyCourses();

  const byKey = useMemo(() => {
    const map = new Map<string, AcademyCourse>();
    for (const entry of courses.all) map.set(entry.course_key, entry);
    return map;
  }, [courses.all]);

  // Independent of the course list — the endpoint is scoped by the tenant on the server, not by a
  // list of keys this app assembled — so the key is stable and the query never refetches because
  // the course list changed shape.
  const issued = useQuery({
    queryKey: ['academy-certificates'],
    queryFn: () => listAcademyCertificates({ pageSize: 200 }),
    staleTime: 60_000,
  });

  const all = issued.data?.results ?? [];

  /**
   * The courses worth offering HERE: the ones that have actually issued something.
   *
   * "Courses with learners" is the wrong list on this screen — fifty enrolled learners and no
   * certificates is still an empty table — and the fan-out above already knows which produced
   * rows, so no extra request is needed to narrow it.
   */
  const choices = useMemo(() => {
    const held = new Map<string, number>();
    for (const row of all) held.set(row.course_id, (held.get(row.course_id) ?? 0) + 1);
    return courseChoices(
      courses.all,
      (entry) => {
        const count = held.get(entry.course_key);
        return count ? `${count} certificate${count === 1 ? '' : 's'}` : null;
      },
      course,
    );
  }, [all, courses.all, course]);

  const available = all.filter(isAvailable);
  const invalidated = all.filter((row) => row.invalidated_by);
  const held = all.filter((row) => !isAvailable(row) && !row.invalidated_by);

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return all
      .filter((row) =>
        shelf === 'all'
          ? true
          : shelf === 'available'
            ? isAvailable(row)
            : shelf === 'invalidated'
              ? Boolean(row.invalidated_by)
              : !isAvailable(row) && !row.invalidated_by,
      )
      .filter((row) => (course ? row.course_id === course : true))
      .filter((row) =>
        term
          ? row.username.toLowerCase().includes(term) ||
            (row.email ?? '').toLowerCase().includes(term) ||
            (byKey.get(row.course_id)?.display_name ?? '').toLowerCase().includes(term)
          : true,
      )
      .sort((a, b) => {
        // Newest first, and rows with no known date last rather than first: an unknown date
        // sorting to the top would put held-back certificates above the ones just issued.
        // `created_date` is the row's own field now, not a joined one — this table carries it
        // directly, unlike the per-holder endpoint the join used to reach for.
        if (a.created_date && b.created_date) return b.created_date.localeCompare(a.created_date);
        if (a.created_date) return -1;
        if (b.created_date) return 1;
        return a.username.localeCompare(b.username);
      });
  }, [all, shelf, course, search, byKey]);

  const columns: Column<AcademyCertificate>[] = [
    {
      key: 'learner',
      header: 'Learner',
      render: (row) => (
        <>
          {row.username}
          {row.email ? <small className="admin-sub">{row.email}</small> : null}
        </>
      ),
    },
    // The course column earns its place only while the table spans courses: with one chosen it
    // is the same name on every row, and the panels below already say which course this is.
    ...(course
      ? []
      : [
          {
            key: 'course',
            header: 'Course',
            render: (row: AcademyCertificate) => (
              <CourseCell course={byKey.get(row.course_id)} courseId={row.course_id} />
            ),
          },
        ]),
    {
      key: 'status',
      header: 'Status',
      render: (row) => {
        if (row.invalidated_by) {
          // WHO revoked it, but not WHEN: `academy/certificates` carries `invalidated_by` from
          // one `CertificateInvalidation` query for the whole page, not the row's date or note —
          // reading those would mean the per-row touch this endpoint exists to remove.
          return (
            <>
              <Pill tone="retired">Revoked</Pill>
              <small className="admin-sub">by {row.invalidated_by}</small>
            </>
          );
        }
        // Nor the allowlist reason: the old per-course endpoint's `special_case` came from
        // synthetic rows under `?filter=granted_exceptions`, which reading the
        // GeneratedCertificate table directly does not produce — there is no row here for a
        // learner who has been allowlisted but has no certificate yet.
        return (
          <Pill tone={isAvailable(row) ? 'active' : 'suspended'}>
            {isAvailable(row) ? 'Available' : row.certificate_status.replace(/_/g, ' ')}
          </Pill>
        );
      },
    },
    {
      key: 'track',
      header: 'Track',
      render: (row) => (
        <Pill tone={row.enrollment_track === 'audit' ? 'suspended' : 'neutral'}>
          {row.enrollment_track || 'unknown'}
        </Pill>
      ),
    },
    {
      key: 'grade',
      header: 'Grade',
      numeric: true,
      render: (row) => {
        const percent = gradePercent(row.grade);
        // A completion-basis certificate stores `grade === ""` — BLANK, deliberately, because
        // `0.0` asserts a measurement and on a course that scores nothing there is no
        // denominator, so "zero" is a category error rather than a lower bound. Printing 0%
        // or a bare dash beside such a certificate reads as a fault, or as a zero the holder
        // did not score.
        //
        // ONLY the blank case. A literal "0.0" is NOT folded in here: on a graded course a
        // learner really can score zero, and reporting that as "Not graded" would be the same
        // mistake pointing the other way — a real measurement hidden behind "not applicable".
        // The 20 legacy rows that stored "0.0" on ungraded courses were corrected to blank by
        // `thinlms_awards_reconcile`, so a "0.0" reaching this cell now means an actual zero.
        return percent === null ? (
          <span className="admin-muted">Not graded</span>
        ) : (
          `${percent}%`
        );
      },
    },
    {
      key: 'issued',
      header: 'Issued',
      render: (row) => {
        // Read straight off the row now, not a second per-holder request — `read_certificates`
        // carries `grade` and `created_date` on every certificate regardless of whether the
        // learner may currently see it, so there is no more "not readable yet" row to report.
        const url = certificateViewUrl(row);
        return (
          <>
            {formatIssued(row.created_date)}
            {/* A plain `<a>`: `/certificates/<uuid>` is the platform's own page, proxied under
                this host, and the router must not answer it. */}
            {url ? (
              <small className="admin-sub">
                <a href={url} target="_blank" rel="noreferrer">
                  View certificate
                </a>
              </small>
            ) : null}
          </>
        );
      },
    },
  ];

  const loading = courses.isPending || issued.isPending;
  const chosen = byKey.get(course);

  return (
    <>
      <PageHeader
        title="Certificates"
        description="Every certificate this academy has issued, who holds it, and how to re-run generation."
      />

      <div className="admin-stack">
        <KpiRow>
          <Kpi label="Certificates held" value={loading ? '—' : all.length} />
          <Kpi
            label="Available to view"
            value={loading ? '—' : available.length}
            hint={held.length > 0 ? `${held.length} earned but held back` : undefined}
          />
          <Kpi
            label="Courses issuing"
            value={loading ? '—' : new Set(all.map((row) => row.course_id)).size}
            hint={courses.withLearners.length ? `of ${courses.withLearners.length} with learners` : undefined}
          />
          <Kpi
            label="Revoked"
            value={loading ? '—' : invalidated.length}
            hint={invalidated.length > 0 ? 'withdrawn after issue' : undefined}
          />
        </KpiRow>

        <section className="admin-panel">
          <Toolbar>
            <Tabs
              label="Certificate state"
              active={shelf}
              onChange={(id) => setShelf(id as Shelf)}
              tabs={[
                { id: 'all', label: 'All', count: loading ? undefined : all.length },
                { id: 'available', label: 'Available', count: loading ? undefined : available.length },
                { id: 'held', label: 'Held back', count: loading ? undefined : held.length },
                { id: 'invalidated', label: 'Revoked', count: loading ? undefined : invalidated.length },
              ]}
            />

            <CourseSwitcher
              choices={choices}
              value={course}
              onChange={setCourse}
              allMeasure={
                loading
                  ? 'Loading…'
                  : `${choices.length} course${choices.length === 1 ? '' : 's'} with certificates`
              }
              hiddenNote={
                courses.all.length > choices.length
                  ? `${courses.all.length - choices.length} of this academy's ${courses.all.length} courses have issued no certificates and are not listed.`
                  : undefined
              }
              disabled={loading}
              searchPlaceholder="Search courses with certificates…"
            />

            <div className="toolbar__end">
              <SearchField value={search} onChange={setSearch} placeholder="Search learner or course" />
            </div>
          </Toolbar>

          {(courses.isError || issued.isError) && (
            <Banner tone="danger">
              <strong>We could not load the certificate list.</strong> Nothing has changed.
              Reload the page to try again — this needs staff access to the academy's courses.
            </Banner>
          )}

          {loading ? (
            <Spinner label="Loading certificates…" />
          ) : (
            <DataTable
              columns={columns}
              rows={rows}
              rowKey={(row) => `${row.username}|${row.course_id}`}
              empty={
                all.length === 0
                  ? 'Nobody in this academy holds a certificate yet.'
                  : 'No certificate matches these filters.'
              }
            />
          )}

          {/*
            The four banners this used to need — courses left unread, courses cut short at one
            page, courses that individually refused, and holders skipped by the per-learner grade
            lookup — were each a symptom of fanning out one request per course and a second per
            holder. Paging by enrolment removes every one of them: the only way this list is
            incomplete now is the ordinary "there is another page", which `count` reports
            precisely, the same fix `AdminGrades` made for the same reason.
          */}
          {issued.data && issued.data.count > issued.data.results.length && (
            <Banner tone="info">
              <strong>
                Showing {issued.data.results.length} of {issued.data.count} certificates.
              </strong>{' '}
              Choose a course to see its whole list, or search to narrow it.
            </Banner>
          )}
        </section>

        {/* Per course, because that is what they act on. Both need a course named, so they
            appear with one and explain themselves without one rather than sitting there
            disabled. */}
        {course ? (
          <CourseLevers courseId={course} courseName={chosen?.display_name ?? course} />
        ) : (
          <section className="admin-panel">
            <header className="admin-panel__head">
              <h2>Re-run generation</h2>
            </header>
            <p className="admin-note">
              Generation runs on one course at a time. Choose a course above to re-issue its
              certificates, see whether it issues them at all, and read the record of previous
              runs.
            </p>
          </section>
        )}
      </div>
    </>
  );
}

/** The two things that belong to a single course: re-running generation, and its record. */
function CourseLevers({ courseId, courseName }: { courseId: string; courseName: string }) {
  const queryClient = useQueryClient();
  const [statuses, setStatuses] = useState<string[]>([]);
  const [confirming, setConfirming] = useState(false);

  const config = useQuery({
    queryKey: ['certificate-config', courseId],
    queryFn: () => getCertificateConfig(courseId),
  });

  const history = useQuery({
    queryKey: ['certificate-history', courseId],
    queryFn: () => listGenerationHistory(courseId),
  });

  const regenerate = useMutation({
    mutationFn: () => regenerateCertificates(courseId, statuses),
    onSuccess: () => {
      setConfirming(false);
      setStatuses([]);
      queryClient.invalidateQueries({ queryKey: ['academy-certificates'] });
      queryClient.invalidateQueries({ queryKey: ['certificate-details'] });
      queryClient.invalidateQueries({ queryKey: ['certificate-history', courseId] });
    },
  });

  const historyRows: GenerationHistoryEntry[] = Array.isArray(history.data)
    ? history.data
    : (history.data?.results ?? []);

  return (
    <>
      {config.data && !config.data.enabled && (
        <Banner tone="warning">
          <strong>{courseName} does not issue certificates.</strong> Set one up on the course's
          own Certificates tab — that is where a certificate's title, description and
          signatories live. Until then nothing will be issued however much anyone completes.
        </Banner>
      )}

      <section className="admin-panel">
        <header className="admin-panel__head">
          <h2>Re-run generation for {courseName}</h2>
        </header>

        <p className="admin-note">
          Choose whose certificates to generate again. This replaces certificates people may
          already have downloaded or linked to, so it is worth being specific.
        </p>

        <div className="admin-checks">
          {REGENERATE_STATUSES.map((option) => (
            <label key={option.value}>
              <input
                type="checkbox"
                checked={statuses.includes(option.value)}
                onChange={(event) =>
                  setStatuses((current) =>
                    event.target.checked
                      ? [...current, option.value]
                      : current.filter((value) => value !== option.value),
                  )
                }
              />
              <span>
                {option.label}
                <small>{option.hint}</small>
              </span>
            </label>
          ))}
        </div>

        <div className="admin-actions">
          <Button
            variant="primary"
            disabled={statuses.length === 0 || regenerate.isPending}
            onClick={() => setConfirming(true)}
          >
            Regenerate
          </Button>
        </div>

        {regenerate.isError && (
          <Banner tone="danger">
            <strong>The academy refused the request.</strong> No certificates were regenerated.
          </Banner>
        )}

        {regenerate.isSuccess && (
          <Banner tone="info">
            <strong>Started.</strong> Generation runs in the background; the list above and the
            history below will show the result once it finishes.
          </Banner>
        )}
      </section>

      <section className="admin-panel">
        <header className="admin-panel__head">
          <h2>Generation history</h2>
        </header>

        {history.isPending ? (
          <Spinner label="Loading history…" />
        ) : historyRows.length === 0 ? (
          <p className="admin-muted">Generation has not been run for this course yet.</p>
        ) : (
          <ul className="admin-files">
            {historyRows.map((entry, index) => (
              <li key={`${entry.task_name ?? 'task'}-${index}`}>
                <span>{entry.task_name ?? 'Certificate generation'}</span>{' '}
                <small className="admin-muted">
                  {entry.created ? formatIssued(entry.created) : ''}
                  {entry.task_state ? ` · ${entry.task_state}` : ''}
                  {entry.requested_by ? ` · asked for by ${entry.requested_by}` : ''}
                </small>
              </li>
            ))}
          </ul>
        )}
      </section>

      {confirming && (
        <ConfirmDialog
          title="Regenerate these certificates?"
          body={
            `Certificates will be generated again for learners in ${courseName} whose ` +
            `certificate is: ${statuses.join(', ')}. Anyone who already holds one gets a new ` +
            `one, and the old link stops working.`
          }
          confirmLabel="Regenerate"
          busy={regenerate.isPending}
          onConfirm={() => regenerate.mutate()}
          onClose={() => setConfirming(false)}
        />
      )}
    </>
  );
}
